// src/components/ProductItem.jsx
import React, { useState, useContext } from 'react';
import { ShopContext } from '../context/ShopContext';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  ToggleButtonGroup,
  ToggleButton,
  styled,
  ButtonGroup,
  Badge,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const StyledToggleButton = styled(ToggleButton)({
  '&.Mui-selected, &.Mui-selected:hover': {
    color: 'white',
    backgroundColor: 'black',
  },
});

const ProductItem = ({ product }) => {
  const { id, name, price, image, sizes = [] } = product;
  const { currency, addToCart, cartItems, removeFromCart } = useContext(ShopContext);
  const navigate = useNavigate();

  const availableSizes = sizes || [];
  const [activeSize, setActiveSize] = useState(availableSizes.length > 0 ? availableSizes[0] : null);

  const handleNavigate = () => {
    navigate(`/product/${id}`);
  };

  const handleSizeChange = (e, newSize) => {
    e.stopPropagation(); // Prevent card's onClick from firing
    if (newSize !== null) {
      setActiveSize(newSize);
    }
  };

  const handleAddToCart = (e) => {
    e.stopPropagation(); // Prevent card's onClick from firing
    if (activeSize) {
      addToCart(id, activeSize);
    } else if (sizes.length > 0) {
      // If there are sizes but none selected, select the first one and add to cart
      const firstSize = sizes[0];
      setActiveSize(firstSize);
      addToCart(id, firstSize);
    } else {
      // If no sizes available, add to cart without size
      addToCart(id, 'one size');
    }
  };

  const handleRemoveFromCart = (e) => {
    e.stopPropagation();
    if (activeSize) {
      removeFromCart(id, activeSize);
    }
  };

  const cartDataForThisProduct = cartItems[id] || {};
  // Calculate total quantity across all sizes for this product
  const totalQuantityInCart = Object.values(cartDataForThisProduct).reduce((sum, qty) => sum + qty, 0);

  return (
    <Card
      onClick={handleNavigate}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        cursor: 'pointer',
        '&:hover .product-image': {
          transform: 'scale(1.05)',
        },
      }}
    >
      <Badge badgeContent={totalQuantityInCart} color="secondary" invisible={totalQuantityInCart === 0}>
        <CardMedia
          component="img"
          className="product-image"
          image={Array.isArray(image) ? image[0] : image}
          alt={name}
          sx={{
            width: '100%',
            aspectRatio: '1 / 1.2',
            objectFit: 'cover',
            transition: 'transform 0.35s ease-in-out',
          }}
        />
      </Badge>

      <CardContent sx={{ flexGrow: 1, p: 2, pb: 0 }}>
        <Typography variant="h6" component="div" sx={{ color: 'text.primary' }}>
          {name}
        </Typography>
        <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold' }}>
          {currency}{price}
        </Typography>
      </CardContent>

      <CardActions sx={{ p: 2, pt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        {availableSizes.length > 0 && (
          <ToggleButtonGroup
            value={activeSize || ''}
            exclusive
            onChange={handleSizeChange}
            aria-label="product size"
            size="small"
            fullWidth
            onClick={(e) => e.stopPropagation()} // Stop propagation on the container as well
          >
            {availableSizes.map((size) => (
              <StyledToggleButton key={size} value={size} aria-label={size}>
                {size}
              </StyledToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
        
        {availableSizes.length === 0 ? (
          // If product has no sizes, show a single add to cart button
          <Button
            variant="contained"
            onClick={handleAddToCart}
            fullWidth
            size="small"
            startIcon={<ShoppingCartIcon />}
          >
            {totalQuantityInCart > 0 ? `Add More (${totalQuantityInCart})` : 'Add to Cart'}
          </Button>
        ) : activeSize ? (
          // If size is selected, show quantity controls
          totalQuantityInCart > 0 ? (
            <ButtonGroup variant="outlined" size="small" fullWidth>
              <Button onClick={handleRemoveFromCart}><RemoveIcon fontSize="small" /></Button>
              <Button disabled sx={{ color: 'text.primary !important' }}>{totalQuantityInCart}</Button>
              <Button onClick={handleAddToCart}><AddIcon fontSize="small" /></Button>
            </ButtonGroup>
          ) : (
            <Button
              variant="contained"
              onClick={handleAddToCart}
              fullWidth
              size="small"
              startIcon={<ShoppingCartIcon />}
            >
              Add to Cart
            </Button>
          )
        ) : (
          // If no size selected but sizes are available
          <Button
            variant="contained"
            onClick={handleAddToCart}
            fullWidth
            size="small"
            startIcon={<ShoppingCartIcon />}
          >
            Add to Cart
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default ProductItem;